import {
  getAgentCallQueryOptions,
  type AgentCallDetail,
  type CallTranscriptMessage,
} from "@/api/get-agent-calls";
import { formatCallDuration, formatTranscriptOffset } from "@/lib/format-call";
import { ConversationTimeDisplay } from "@rulebase/core/components";
import { useQuery } from "@tanstack/react-query";
import { IconChevronLeft } from "central-icons";
import { CopyButton } from "./ui/copy-button";
import { InitialsAvatar } from "./ui/initials-avatar";
import { Markdown } from "./ui/markdown";
import { Skeleton } from "./ui/skeleton";

const SPEAKER_LABELS: Record<CallTranscriptMessage["speaker"], string> = {
  agent: "Agent",
  customer: "Customer",
  unknown: "Speaker",
};

function formatTranscript(transcript: CallTranscriptMessage[]): string {
  return transcript
    .map((message) => `${SPEAKER_LABELS[message.speaker]}: ${message.text}`)
    .join("\n");
}

/**
 * A single call, opened from the recent calls list: what was said, and the
 * summary api2 generated from it. Both are copyable because the common next
 * step is pasting one of them into a Zendesk reply.
 */
export function CallDetailView(props: { callId: string; onBack: () => void }) {
  const { data, isPending, isError } = useQuery(
    getAgentCallQueryOptions(props.callId),
  );

  return (
    <div className="flex h-screen flex-col overflow-y-auto">
      <div className="mx-auto flex w-full max-w-3xl flex-col px-2 py-6">
        <button
          type="button"
          onClick={props.onBack}
          className="flex cursor-pointer items-center gap-1.5 self-start rounded-md bg-neutral-100 py-1 pr-2.5 pl-1.5 font-medium text-neutral-600 transition-colors hover:bg-neutral-200"
        >
          <IconChevronLeft className="size-3.5" />
          Back to home
        </button>

        {isPending && <Skeleton className="mt-5 h-64 w-full" />}

        {isError && (
          <p className="mt-5 text-neutral-500">
            We couldn&apos;t load this call. Try going back and opening it
            again.
          </p>
        )}

        {data && <CallDetail call={data.data} />}
      </div>
    </div>
  );
}

function CallDetail(props: { call: AgentCallDetail }) {
  const { call } = props;
  const duration = formatCallDuration(call.duration_ms);
  const transcript = call.transcript ?? [];

  return (
    <>
      <div className="mt-4 flex w-full items-center gap-2.5">
        <InitialsAvatar name={call.customer_name} className="size-7" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium text-neutral-700">
            Call with {call.customer_name ?? "unknown caller"}
          </span>
          <span className="flex items-center gap-1.5 text-neutral-500">
            {call.company_name && <span>{call.company_name}</span>}
            {call.company_name && call.started_at && <Dot />}
            {call.started_at && (
              <ConversationTimeDisplay date={call.started_at} variant="long" />
            )}
            {duration && (call.started_at || call.company_name) && <Dot />}
            {duration && <span>{duration}</span>}
          </span>
        </div>
      </div>

      <section className="mt-4 flex w-full flex-col gap-1.5">
        <SectionHeader title="Summary">
          {call.summary && (
            <GhostCopyButton getText={() => call.summary ?? ""} />
          )}
        </SectionHeader>

        {call.summary ? (
          <Markdown className="prose prose-neutral w-full max-w-full text-sm leading-relaxed text-neutral-700 [&_h1]:text-base [&_h1]:font-medium [&_h2]:text-base [&_h2]:font-medium [&_h3]:text-base [&_h3]:font-medium [&_h4]:text-base [&_h4]:font-medium [&_strong]:font-medium">
            {call.summary}
          </Markdown>
        ) : (
          <p className="text-neutral-700">
            The summary for this call isn&apos;t ready yet.
          </p>
        )}
      </section>

      <section className="mt-5 flex w-full flex-col border-t border-neutral-200 pt-4">
        <SectionHeader title="Transcript">
          {transcript.length > 0 && (
            <GhostCopyButton getText={() => formatTranscript(transcript)} />
          )}
        </SectionHeader>

        {transcript.length === 0 ? (
          <p className="mt-2 text-neutral-500">
            This call hasn&apos;t been transcribed yet.
          </p>
        ) : (
          <div className="mt-2 flex flex-col">
            {transcript.map((message, index) => (
              <TranscriptTurn
                // Turns have no ids of their own; offset plus position is
                // stable for a given call.
                key={`${message.start_ms}-${index}`}
                message={message}
                agentName={call.agent?.name}
                customerName={call.customer_name}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function SectionHeader(props: { title: string; children?: React.ReactNode }) {
  return (
    <div className="flex w-full items-center justify-between">
      <h2 className="font-medium text-neutral-700">{props.title}</h2>
      {props.children}
    </div>
  );
}

/**
 * Pulled 7px right so its label lines up with the content edge rather than
 * sitting short of it by its own padding.
 */
function GhostCopyButton(props: {
  getText: () => string | Promise<string>;
}) {
  return (
    <CopyButton
      variant="ghost"
      size="sm"
      className="-mr-2 flex-row-reverse text-neutral-500"
      getText={props.getText}
    >
      Copy
    </CopyButton>
  );
}

function TranscriptTurn(props: {
  message: CallTranscriptMessage;
  agentName: string | null | undefined;
  customerName: string | null | undefined;
}) {
  const { message } = props;
  const isAgent = message.speaker === "agent";
  const speakerName = isAgent
    ? "You"
    : (props.customerName ?? SPEAKER_LABELS[message.speaker]);

  return (
    <div className="flex w-full gap-3 py-1.5">
      {/* 12px/16px sits in a shorter line box than the 14px/20px speaker name
        * beside it, so a couple of pixels bring the two baselines together. */}
      <span className="w-10 shrink-0 pt-0.5 text-xs text-neutral-500 tabular-nums">
        {formatTranscriptOffset(message.start_ms)}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className={
            isAgent
              ? "font-medium text-[#3F6B4E]"
              : "font-medium text-neutral-700"
          }
        >
          {speakerName}
        </span>
        <p className="text-secondary-foreground whitespace-pre-wrap">
          {message.text}
        </p>
      </div>
    </div>
  );
}

function Dot() {
  return <span className="size-0.5 rounded-full bg-neutral-400" />;
}
